// ─────────────────────────────────────────────────────────────────────
// LIVE PLAYERS — the player list a single match is calculated against.
//
// Moved verbatim (#217) out of the `liveAllPlayers` useMemo in
// apps/web/src/state/useDerivations.ts. Nothing about the computation changed;
// it was a pure function already, wrapped in a hook.
//
// WHY IT HAD TO MOVE. The browser calculates a single match's ELO against
// liveAllPlayers and APPLIES it to allPlayers — two different lists, on
// purpose (see useSingleMatchActions: applying to the live list would bake the
// active tournaments' delta in twice when they are later archived). A server
// that calculated against the stored list instead would not be "close enough":
// it changes the rating. Beyond the elo itself, `matchesPlayed` and
// `lastMatchAt` feed getPlayerKMultiplier's newbie and returning-player
// branches, so the multiplier can differ too.
//
// Inputs are exactly the three the memo depended on — allPlayers, the active
// tournament, the paused tournaments — and NOT activeSeries, which the
// dependency array deliberately omits.
// ─────────────────────────────────────────────────────────────────────

export interface LivePlayersInput {
  allPlayers: any[];
  tournament: any;
  pausedTournaments: any[];
}

/**
 * `allPlayers` plus the accumulated ELO / matchesPlayed / lastMatchAt deltas
 * from the active and paused tournaments (pair-aware for KM and seriespill).
 */
export function liveAllPlayersOf({
  allPlayers,
  tournament,
  pausedTournaments,
}: LivePlayersInput): any[] {
  // Collect all ELO deltas from the active + paused tournaments per playerId
  const deltas = new Map(); // playerId → accumulated delta
  // Collect the matchesPlayed delta from the active + paused tournaments per playerId.
  // In active tournaments tournament.players[i].matchesPlayed is already
  // incremented for each completed match, but the difference from the start value
  // (which we don't always have) is impractical. We therefore count completed
  // matches in tournament.matches directly where the player is involved.
  const matchCounts = new Map(); // playerId → number of completed matches in active tournaments
  const lastMatchTimes = new Map(); // playerId → latest playedAt

  const addDelta = (t: any) => {
    if (!t) return;
    // ── Klubbmesterskap/seriespill: ELO lives on the matches' teamEloChange (pair structure),
    //    not on t.players. Sum the change per player from played matches. ──
    if (t.type === 'klubbmesterskap' || t.type === 'seriespill') {
      const pairById = new Map((t.pairs || []).map((p) => [p.id, p]));
      (t.matches || []).forEach((m) => {
        if (!m.score || m.score.walkover || !m.teamEloChange) return;
        const ts = m.completedAt || 0;
        const apply = (pair: any, change: any) => {
          if (!pair || change == null) return;
          [pair.p1Id, pair.p2Id].filter(Boolean).forEach((pid) => {
            deltas.set(pid, (deltas.get(pid) || 0) + change);
            matchCounts.set(pid, (matchCounts.get(pid) || 0) + 1);
            if (ts > (lastMatchTimes.get(pid) || 0)) lastMatchTimes.set(pid, ts);
          });
        };
        apply(pairById.get(m.team1?.pairId), m.teamEloChange.t1);
        apply(pairById.get(m.team2?.pairId), m.teamEloChange.t2);
      });
      return;
    }
    if (!Array.isArray(t.players)) return;
    t.players.forEach((tp) => {
      let startElo = tp.startElo;
      if (startElo == null && Array.isArray(t.matches)) {
        const allMatches = [...(t.matches || []), ...(t.finalMatches || [])];
        const firstWithPre = allMatches
          .filter((m) => m.completed && m.playerEloPre?.[tp.id] != null)
          .sort(
            (a, b) => (a.playedAt || a.completedAt || 0) - (b.playedAt || b.completedAt || 0),
          )[0];
        startElo = firstWithPre?.playerEloPre?.[tp.id];
      }
      if (startElo == null || tp.elo == null) return;
      const delta = tp.elo - startElo;
      deltas.set(tp.id, (deltas.get(tp.id) || 0) + delta);
    });

    // Count completed matches in this tournament per player
    const allMatches = [...(t.matches || []), ...(t.finalMatches || [])];
    allMatches.forEach((m) => {
      if (!m.completed) return;
      const ts = m.playedAt || m.completedAt || 0;
      // Padel24 slot-format
      const t1 = [m.team1?.p1, m.team1?.p2].filter((s) => s?.kind === 'concrete');
      const t2 = [m.team2?.p1, m.team2?.p2].filter((s) => s?.kind === 'concrete');
      const ids = [
        ...t1.map((s) => s.playerId),
        ...t2.map((s) => s.playerId),
        // Americano flat-format
        m.team1?.p1Id,
        m.team1?.p2Id,
        m.team2?.p1Id,
        m.team2?.p2Id,
      ].filter(Boolean);
      ids.forEach((id) => {
        matchCounts.set(id, (matchCounts.get(id) || 0) + 1);
        if (ts > (lastMatchTimes.get(id) || 0)) lastMatchTimes.set(id, ts);
      });
    });
  };

  // Active tournament (not archived)
  if (tournament && !tournament.isArchived) {
    addDelta(tournament);
  }
  // All paused tournaments
  pausedTournaments.forEach(addDelta);

  // Build a new allPlayers with adjusted elo + matchesPlayed/lastMatchAt from active tournaments
  return allPlayers.map((p) => {
    const d = deltas.get(p.id);
    const mc = matchCounts.get(p.id);
    const lt = lastMatchTimes.get(p.id);
    if (!d && !mc) return p;
    return {
      ...p,
      elo: (p.elo || 0) + (d || 0),
      matchesPlayed: (p.matchesPlayed || 0) + (mc || 0),
      lastMatchAt: lt > (p.lastMatchAt || 0) ? lt : p.lastMatchAt,
    };
  });
}
